// ─── Guided Setup: guard patrol, set up in full ───────────────────────────────
//
// A round, its hours, and every checkpoint on it — so the whole thing is defined
// here rather than one checkpoint here and the rest on the Guard Patrol page.
//
// Two things this panel refuses to fudge:
//
//  • A checkpoint needs a drawn zone. It is the membership test — without a
//    polygon there is nothing to be inside, and the checkpoint silently never
//    completes, so every round it belongs to reports MISSED.
//
//  • Who gets told. A missed round that notifies nobody is a log entry, not a
//    patrol system, and the supervisor list is the only part of this a customer
//    cannot infer later from the report.
//
// Checkpoint check-in is a raised hand held for a couple of seconds, with the
// face used only to say WHO checked in (a face that fails to match is not a
// failed checkpoint). Night-IR pose reliability is the known risk here — worth
// testing on a real night before trusting a round.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/streamApi';
import PatrolZoneEditor from '../PatrolZoneEditor';
import { Field, Panel, SettingRow, DayPicker, Advanced, countChanged, useModuleSaver }
  from './SetupBits';

// Site-wide, defaulted. `def` matches guard_patrol.default_config().
const SITE_ADVANCED = [
  { key: 'retention_days', label: 'Keep patrol records for (days)', type: 'int',
    min: 1, max: 1825, step: 30, def: 90 },
];

const blankCheckpoint = cam => ({
  name: '', camera_idx: cam, patrol_zone: [], expected_guard: null,
  min_presence_seconds: 3, gesture_hold_seconds: 2, face_checkin: true,
});

const blankRoute = cam => ({
  name: '', active: true, supervisor_override: [],
  schedule: { start: '22:00', end: '06:00', frequency_minutes: 30,
              early_window_seconds: 300, late_window_seconds: 300,
              active_days: [0, 1, 2, 3, 4, 5, 6] },
  checkpoints: [blankCheckpoint(cam)],
});

// How often a round is walked is the site's own rule, so it is asked. The grace
// windows have a sensible default (5 min either side) and sit under Advanced.
// `def` matches guard_patrol._normalize_schedule.
const SCHEDULE_ASK = [
  { key: 'frequency_minutes', label: 'A round comes due every (min)', type: 'int',
    min: 10, max: 720, step: 10, def: 30, ask: true },
];
const SCHEDULE_ADVANCED = [
  { key: 'early_window_seconds', label: 'Allowed early by (s)', type: 'int',
    min: 0, max: 1800, step: 60, def: 300,
    hint: 'A guard who arrives inside this still counts as on time.' },
  { key: 'late_window_seconds', label: 'Allowed late by (s)', type: 'int',
    min: 0, max: 1800, step: 60, def: 300,
    hint: 'After this the checkpoint is MISSED and the supervisor is told.' },
];
// Per-checkpoint, all defaulted — the camera, the zone and the expected guard are
// the questions. `def` matches guard_patrol._normalize_checkpoint.
const CHECKPOINT_ADVANCED = [
  { key: 'min_presence_seconds', label: 'Seconds inside the area', type: 'range',
    min: 0.5, max: 15, step: 0.5, def: 3 },
  { key: 'gesture_hold_seconds', label: 'Hand held up for (s)', type: 'range',
    min: 0.5, max: 6, step: 0.5, def: 2,
    hint: 'Longer is harder to trigger by accident and harder to do at night, when the infrared picture makes a hand hard to find.' },
  { key: 'face_checkin', label: 'The expected guard’s face alone can complete it', type: 'bool',
    def: true,
    hint: 'With this on, a recognised expected guard standing in the area counts even without the hand raise.' },
];

export default function PatrolPanel({ cameras, onChanged }) {
  const [cfg,     setCfg]     = useState(null);
  const [guards,  setGuards]  = useState([]);     // enrolled Known Faces
  const [sups,    setSups]    = useState([]);     // web users who can be supervisors
  const [route,   setRoute]   = useState(null);   // the route being added / changed
  const [zoneFor, setZoneFor] = useState(null);   // index of the checkpoint being drawn
  const { busy, err, note, setErr, run } = useModuleSaver(apiFetch, onChanged);

  const load = useCallback(() => apiFetch('/ui/patrol/config')
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) setCfg(d); })
    .catch(() => setErr('Could not read the patrol settings.')), [setErr]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/ui/patrol/form-options')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        setGuards(Array.isArray(d?.guards) ? d.guards : []);
        setSups(Array.isArray(d?.supervisors) ? d.supervisors : []);
      })
      .catch(() => {});
  }, []);

  const reload = async () => { await load(); if (onChanged) await onChanged(); };
  const routes = cfg?.routes || [];
  const camOf  = i => cameras.find(c => c.idx === i)?.name || `Camera ${i}`;

  const saveSetting = (key, val) =>
    run('PUT', '/ui/patrol/settings', { [key]: val }, null).then(d => d && setCfg(d));

  async function saveRoute() {
    const body = {
      name: (route.name || '').trim() || 'Patrol Route',
      active: route.active !== false,
      supervisor_override: route.supervisor_override || [],
      schedule: route.schedule,
      checkpoints: (route.checkpoints || []).map((c, i) => ({
        ...c,
        name: (c.name || '').trim() || `Checkpoint ${i + 1}`,
        expected_guard: c.expected_guard || null,
      })),
    };
    const ok = route.id
      ? await run('PUT', `/ui/patrol/routes/${route.id}`, body, 'this round')
      : await run('POST', '/ui/patrol/routes', body, 'this round');
    if (ok) { setRoute(null); setZoneFor(null); await reload(); }
  }

  async function removeRoute(id) {
    if (!window.confirm('Remove this round? Rounds already recorded are kept.')) return;
    if (await run('DELETE', `/ui/patrol/routes/${id}`, undefined, 'the change')) await reload();
  }

  const patchCp = (i, patch) => setRoute(r => ({
    ...r, checkpoints: r.checkpoints.map((c, n) => (n === i ? { ...c, ...patch } : c)),
  }));
  const allZoned = (route?.checkpoints || []).every(c => (c.patrol_zone || []).length >= 3);

  // ── drawing one checkpoint's zone, full width ──
  if (route && zoneFor !== null) {
    const cp = route.checkpoints[zoneFor];
    return (
      <>
        <div className="gs-section-title">
          Where does the guard stand at “{cp.name || `Checkpoint ${zoneFor + 1}`}”?
        </div>
        <p className="gs-help">
          Tap the corners of the spot they stand in to raise a hand. Make it the floor area
          they actually stop on — a zone drawn around a doorway at the far end of a corridor
          is hard to stand inside, and the check-in will not register.
        </p>
        <Field label="Camera">
          <select className="gs-input" value={cp.camera_idx}
                  onChange={e => patchCp(zoneFor, { camera_idx: Number(e.target.value), patrol_zone: [] })}>
            {cameras.map(c => <option key={c.idx} value={c.idx}>{c.name}</option>)}
          </select>
        </Field>
        <PatrolZoneEditor cameraIdx={cp.camera_idx} value={cp.patrol_zone || []}
                          onChange={z => patchCp(zoneFor, { patrol_zone: z })} />
        <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
          <button className="gs-btn primary" onClick={() => setZoneFor(null)}
                  disabled={(cp.patrol_zone || []).length < 3}>
            Done with this area
          </button>
          <button className="gs-btn" onClick={() => patchCp(zoneFor, { patrol_zone: [] })}>
            Start over
          </button>
          {(cp.patrol_zone || []).length < 3 &&
            <span className="gs-foot-note">Three corners is enough</span>}
        </div>
      </>
    );
  }

  // ── the route editor ──
  if (route) {
    return (
      <>
        <div className="gs-section-title">{route.id ? `Change “${route.name}”` : 'New round'}</div>

        <Field label="What is this round called?">
          <input className="gs-input" value={route.name} maxLength={40} placeholder="e.g. Night Round"
                 onChange={e => setRoute(r => ({ ...r, name: e.target.value }))} />
        </Field>

        <div className="gs-input-row" style={{ maxWidth: 420 }}>
          <Field label="From"><input className="gs-input" type="time" value={route.schedule.start}
            onChange={e => setRoute(r => ({ ...r, schedule: { ...r.schedule, start: e.target.value } }))} /></Field>
          <Field label="To"><input className="gs-input" type="time" value={route.schedule.end}
            onChange={e => setRoute(r => ({ ...r, schedule: { ...r.schedule, end: e.target.value } }))} /></Field>
        </div>
        <Field label="On which nights?">
          <DayPicker value={route.schedule.active_days}
                     onChange={d => setRoute(r => ({ ...r, schedule: { ...r.schedule, active_days: d } }))} />
        </Field>
        {SCHEDULE_ASK.map(s => (
          <SettingRow key={s.key} spec={s} value={route.schedule[s.key]}
                      onChange={v => setRoute(r => ({ ...r, schedule: { ...r.schedule, [s.key]: v } }))} />
        ))}
        <Advanced title="Advanced timing" changed={countChanged(SCHEDULE_ADVANCED, route.schedule)}>
          {SCHEDULE_ADVANCED.map(s => (
            <SettingRow key={s.key} spec={s} value={route.schedule[s.key]}
                        onChange={v => setRoute(r => ({ ...r, schedule: { ...r.schedule, [s.key]: v } }))} />
          ))}
        </Advanced>

        <div className="gs-section-title">Checkpoints on this round</div>
        <p className="gs-help">
          Each one is a place the guard must reach and raise a hand. They are all checked
          every time the round comes due, and any that nobody reaches is reported missed.
        </p>

        {route.checkpoints.map((c, i) => {
          const zoned = (c.patrol_zone || []).length >= 3;
          return (
            <div className="gs-panel" key={i} style={{ marginBottom: 14 }}>
              <div className="gs-panel-title">Checkpoint {i + 1}</div>
              <div className="gs-input-row" style={{ maxWidth: 640 }}>
                <Field label="Name">
                  <input className="gs-input" value={c.name} maxLength={40}
                         placeholder={`Checkpoint ${i + 1}`}
                         onChange={e => patchCp(i, { name: e.target.value })} />
                </Field>
                <Field label="Camera">
                  <select className="gs-input" value={c.camera_idx}
                          onChange={e => patchCp(i, { camera_idx: Number(e.target.value), patrol_zone: [] })}>
                    {cameras.map(x => <option key={x.idx} value={x.idx}>{x.name}</option>)}
                  </select>
                </Field>
                {guards.length > 0 && (
                  <Field label="Who is expected?">
                    <select className="gs-input" value={c.expected_guard || ''}
                            onChange={e => patchCp(i, { expected_guard: e.target.value || null })}>
                      <option value="">Anyone</option>
                      {guards.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </Field>
                )}
              </div>
              <div className="gs-row" style={{ borderBottom: 'none' }}>
                <span className="gs-row-icon">{zoned ? '📐' : '⚠️'}</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">
                    {zoned ? 'Check-in area drawn' : 'No check-in area yet'}
                  </span>
                  <span className="gs-row-sub">
                    {zoned
                      ? `On ${camOf(c.camera_idx)} · ${c.patrol_zone.length} corners`
                      : 'Without it this checkpoint can never be completed — every round would report it missed'}
                  </span>
                </span>
                <button className="gs-btn small" onClick={() => setZoneFor(i)}>
                  {zoned ? 'Redraw' : 'Draw it'}
                </button>
                {route.checkpoints.length > 1 && (
                  <button className="gs-btn small ghost"
                          onClick={() => setRoute(r => ({
                            ...r, checkpoints: r.checkpoints.filter((_, n) => n !== i) }))}>
                    Remove
                  </button>
                )}
              </div>
              <Advanced title="Advanced check-in rules" changed={countChanged(CHECKPOINT_ADVANCED, c)}>
                {CHECKPOINT_ADVANCED.map(s => (
                  <SettingRow key={s.key} spec={s} value={c[s.key]}
                              onChange={v => patchCp(i, { [s.key]: v })} />
                ))}
              </Advanced>
            </div>
          );
        })}

        <p className="gs-help gs-help-after">
          <button className="gs-btn small"
                  onClick={() => setRoute(r => ({
                    ...r, checkpoints: [...r.checkpoints, blankCheckpoint(cameras[0]?.idx ?? 0)] }))}>
            Add another checkpoint
          </button>
        </p>

        {sups.length > 0 && (
          <>
            <div className="gs-section-title">Who is told about this round?</div>
            <p className="gs-help">
              Leave these alone to use the site’s supervisors. They are notified when a
              checkpoint is missed, and when somebody other than the expected guard checks in.
            </p>
            <div className="gs-chips">
              {sups.map(s => {
                const on = (route.supervisor_override || []).includes(s.id);
                return (
                  <button key={s.id} type="button" className={`gs-chip${on ? ' on' : ''}`}
                          onClick={() => setRoute(r => ({
                            ...r,
                            supervisor_override: on
                              ? r.supervisor_override.filter(x => x !== s.id)
                              : [...(r.supervisor_override || []), s.id],
                          }))}>
                    {s.username}
                  </button>
                );
              })}
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
          <button className="gs-btn primary" onClick={saveRoute} disabled={busy || !allZoned}>
            {busy ? <span className="gs-spin" /> : (route.id ? 'Save changes' : 'Add this round')}
          </button>
          <button className="gs-btn ghost" onClick={() => { setRoute(null); setErr(null); }}>Cancel</button>
          {!allZoned && <span className="gs-foot-note">Every checkpoint needs its area drawn</span>}
        </div>
        {err && <div className="gs-err" role="alert">{err}</div>}
      </>
    );
  }

  // ── the list ──
  return (
    <>
      {!cfg && !err && <div className="gs-panel"><div className="gs-scanning"><span className="gs-spin" /> Loading…</div></div>}

      {cfg && (
        <>
          <div className="gs-section-title">Rounds</div>
          <p className="gs-help">
            A guard reaches each checkpoint and raises a hand to the camera. The round is
            checked on a timer, and a checkpoint nobody reached is reported — with the
            photo — to whoever you name below.
          </p>

          {routes.length === 0 && (
            <div className="gs-empty">No rounds yet. Nothing is checked until there is one.</div>
          )}

          {routes.map(r => {
            const cps   = r.checkpoints || [];
            const zoned = cps.filter(c => (c.patrol_zone || []).length >= 3).length;
            return (
              <div className={`gs-row${zoned === cps.length ? '' : ' gs-row-bad'}`} key={r.id}>
                <span className="gs-row-icon">{zoned === cps.length ? '🛡️' : '⚠️'}</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">{r.name}{r.active === false ? ' (paused)' : ''}</span>
                  <span className="gs-row-sub">
                    {r.schedule?.start}–{r.schedule?.end} · every {r.schedule?.frequency_minutes}min ·{' '}
                    {cps.length} checkpoint{cps.length === 1 ? '' : 's'}
                    {zoned === cps.length ? '' : ` · ${cps.length - zoned} with no area drawn`}
                  </span>
                </span>
                <button className="gs-btn small" onClick={() => setRoute({
                  ...blankRoute(cameras[0]?.idx ?? 0), ...r,
                  schedule: { ...blankRoute(0).schedule, ...(r.schedule || {}) },
                  checkpoints: (r.checkpoints || []).length ? r.checkpoints
                                                            : [blankCheckpoint(cameras[0]?.idx ?? 0)],
                })}>Change</button>
                <button className="gs-btn small ghost" onClick={() => removeRoute(r.id)}>Remove</button>
              </div>
            );
          })}

          <p className="gs-help gs-help-after">
            <button className="gs-btn small primary"
                    onClick={() => setRoute(blankRoute(cameras[0]?.idx ?? 0))}>
              Add a round
            </button>
          </p>

          <div className="gs-section-title">Supervisors</div>
          {sups.length === 0 ? (
            <Panel tone="warn" title="Nobody can be notified yet">
              A missed round is reported to a dashboard user, and this box has none besides
              the administrator. Add the supervisors as users first — a round that notifies
              nobody is only a log entry.
            </Panel>
          ) : (
            <>
              <p className="gs-help">
                Told when a checkpoint is missed, and when somebody other than the expected
                guard checks in. A round can name its own people instead.
              </p>
              <div className="gs-chips">
                {sups.map(s => {
                  const on = (cfg.supervisors || []).includes(s.id);
                  return (
                    <button key={s.id} type="button" className={`gs-chip${on ? ' on' : ''}`}
                            disabled={busy}
                            onClick={() => saveSetting('supervisors', on
                              ? (cfg.supervisors || []).filter(x => x !== s.id)
                              : [...(cfg.supervisors || []), s.id])}>
                      {s.username}
                    </button>
                  );
                })}
              </div>
              {(cfg.supervisors || []).length === 0 && (
                <p className="gs-help gs-help-after">
                  Nobody chosen — missed rounds will be recorded but will not reach anyone.
                </p>
              )}
            </>
          )}

          <Advanced changed={countChanged(SITE_ADVANCED, cfg)}>
            {SITE_ADVANCED.map(s => (
              <SettingRow key={s.key} spec={s} value={cfg[s.key]}
                          onChange={v => saveSetting(s.key, v)} />
            ))}
          </Advanced>

          <Panel title="Worth knowing before you rely on it">
            Check-in is a raised hand, held for about two seconds, inside the area you drew.
            The face is used only to say <em>who</em> checked in — a face the system cannot
            place does not fail the checkpoint. Hand detection at night depends on the
            camera’s infrared picture, so test one real round after dark before trusting it.
          </Panel>

          {note && <p className="gs-help gs-help-after">{note}</p>}
          {err && <div className="gs-err" role="alert">{err}</div>}
        </>
      )}
    </>
  );
}
